import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Loader2,
  Paperclip,
  Trash2,
} from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FileUpload,
  Skeleton,
  toast,
} from '@/components/cargofl-ui';
import {
  attachmentUrl,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
  type Attachment,
  type AttachmentEntity,
} from '@/lib/attachments-api';
import { EmptyState } from '@/components/empty-state';
import { FilesArt } from '@/components/illustrations';
import { cn } from '@/lib/utils';

/**
 * For the field inside an `EvidenceField` that can upload: the box around it owns
 * the border, shadow and focus ring, so the field must draw none of its own.
 */
export const EVIDENCE_FIELD_CLASS = 'resize-none border-0 shadow-none focus-visible:ring-0';

/** Mirrors the API's allowlist, so the file picker offers what the server accepts. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.doc,.docx,.xls,.xlsx';

function fileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Upload/remove/list for one record, so the card and the inline field behave alike. */
function useEvidence(entityType: AttachmentEntity, entityId: string) {
  const { data, isPending } = useAttachments(entityType, entityId);
  const upload = useUploadAttachment(entityType, entityId);
  const remove = useDeleteAttachment(entityType, entityId);
  const rows = data ?? [];
  const previewables = rows.filter((f) => previewable(f.mime));
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  async function onFiles(files: File[]) {
    for (const file of files) {
      try {
        await upload.mutateAsync(file);
        toast.success(`${file.name} attached`);
      } catch (e) {
        toast.error(`Could not attach ${file.name}`, {
          description: e instanceof Error ? e.message : undefined,
        });
      }
    }
  }

  async function onDelete(id: string, filename: string) {
    try {
      await remove.mutateAsync(id);
      toast.success(`${filename} removed`);
    } catch (e) {
      toast.error('Could not remove it', {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  return {
    rows,
    isPending,
    onFiles,
    onDelete,
    uploading: upload.isPending,
    removing: remove.isPending,
    /** The dialog, wired: mount it once wherever the rows are drawn. */
    preview: (
      <EvidencePreview
        files={previewables}
        index={previewIndex}
        onIndex={setPreviewIndex}
        onClose={() => setPreviewIndex(null)}
      />
    ),
    openPreview: (file: Attachment) =>
      setPreviewIndex(previewables.findIndex((f) => f.id === file.id)),
  };
}

/** What the browser can draw itself. Office files still go through Download. */
const previewable = (mime: string) => mime.startsWith('image/') || mime === 'application/pdf';

/**
 * The file, in a dialog, without leaving the page.
 *
 * Fetched to a blob rather than pointed at directly: the API serves every file as
 * `Content-Disposition: attachment` on purpose (see `AttachmentsController`), so an
 * `<iframe src>` at that URL would trigger a download instead of rendering. A blob
 * keeps the API's own decision intact - the bytes are typed by the mime the API
 * allowlisted, and nothing uploaded is ever navigated to on the API's origin.
 */
function EvidencePreview({
  files,
  index,
  onIndex,
  onClose,
}: {
  /** Only the previewable ones, in list order - ← / → walk this. */
  files: Attachment[];
  index: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const file = index === null ? null : (files[index] ?? null);
  const [url, setUrl] = useState<string | null>(null);

  const prev = index !== null && index > 0 ? index - 1 : null;
  const next = index !== null && index < files.length - 1 ? index + 1 : null;

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && prev !== null) onIndex(prev);
      if (e.key === 'ArrowRight' && next !== null) onIndex(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, prev, next, onIndex]);

  useEffect(() => {
    if (!file) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    fetch(attachmentUrl(file.id), { credentials: 'include' })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) toast.error(`Could not open ${file.filename}`);
      });
    return () => {
      cancelled = true;
      setUrl(null);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{file?.filename}</DialogTitle>
        </DialogHeader>
        {!url ? (
          <Skeleton className="h-[70vh] w-full" />
        ) : file?.mime === 'application/pdf' ? (
          <iframe src={url} title={file.filename} className="h-[70vh] w-full rounded-md border" />
        ) : (
          <img
            src={url}
            alt={file?.filename}
            className="mx-auto max-h-[70vh] w-auto rounded-md object-contain"
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {files.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={prev === null}
                  aria-label="Previous file"
                  onClick={() => prev !== null && onIndex(prev)}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="pms-num text-xs text-muted-foreground">
                  {(index ?? 0) + 1} of {files.length}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={next === null}
                  aria-label="Next file"
                  onClick={() => next !== null && onIndex(next)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </>
            )}
          </div>
          {file && (
            <Button hint={"Download this file"} asChild variant="outline" size="sm" className="gap-1.5">
              <a href={attachmentUrl(file.id)} download>
                <Download className="size-4" />
                Download
              </a>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentRow({
  file,
  onDelete,
  onPreview,
  removing,
}: {
  file: Attachment;
  onDelete: (id: string, filename: string) => void;
  onPreview: (file: Attachment) => void;
  removing: boolean;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{file.filename}</p>
        <p className="truncate text-xs text-muted-foreground">
          {fileSize(file.sizeBytes)}
          {file.uploadedByName ? ` · ${file.uploadedByName}` : ''} ·{' '}
          {new Date(file.createdAt).toLocaleDateString()}
        </p>
      </div>
      {/* Preview where the browser can draw it; the download lives inside the preview.
          Office files get the download here, since there is nothing to preview. */}
      {previewable(file.mime) ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={`Preview ${file.filename}`}
          onClick={() => onPreview(file)}
        >
          <Eye className="size-4" />
        </Button>
      ) : (
        <Button hint={"Download"} asChild variant="ghost" size="icon" className="size-8">
          <a href={attachmentUrl(file.id)} download aria-label={`Download ${file.filename}`}>
            <Download className="size-4" />
          </a>
        </Button>
      )}
      {file.canDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-destructive"
          disabled={removing}
          aria-label={`Remove ${file.filename}`}
          onClick={() => onDelete(file.id, file.filename)}
        >
          <Trash2 className="size-4" />
        </Button>
      )}
    </div>
  );
}

/**
 * Evidence attached to one record, without card chrome.
 *
 * `canUpload` only decides whether the drop zone is drawn - the API re-checks the
 * same thing, including refusing an employee's upload once their answers are
 * locked. Hiding the control is a courtesy so nobody drags a file onto a box that
 * was always going to reject it.
 */
function EvidenceList({
  entityType,
  entityId,
  canUpload,
}: {
  entityType: AttachmentEntity;
  entityId: string;
  canUpload: boolean;
}) {
  const { rows, isPending, onFiles, onDelete, removing, preview, openPreview } = useEvidence(
    entityType,
    entityId,
  );

  return (
    <div className="space-y-3">
      {isPending && <Skeleton className="h-10 w-full" />}

      {!isPending && rows.length === 0 && !canUpload && (
        <EmptyState size="sm" art={FilesArt} title="Nothing attached" />
      )}

      {rows.map((file) => (
        <AttachmentRow
          key={file.id}
          file={file}
          onDelete={onDelete}
          onPreview={openPreview}
          removing={removing}
        />
      ))}
      {preview}

      {canUpload && (
        <FileUpload
          documentType="evidence"
          multiple
          accept={ACCEPT}
          onFiles={(files) => void onFiles(files)}
        />
      )}
    </div>
  );
}

/**
 * Evidence attached to the field it backs up: the field is the drop target, with an
 * attach bar under it and the files below that.
 *
 * A dropzone of its own costs ~110px per KRA per stage - four columns of it on one
 * row pushed the ratings they belong to off the screen. Nothing is lost by shrinking
 * it: dragging a file onto the box you just typed in is the same gesture.
 */
export function EvidenceField({
  entityType,
  entityId,
  canUpload,
  children,
}: {
  entityType: AttachmentEntity;
  entityId: string;
  canUpload: boolean;
  /** The field the evidence belongs to - a textarea or note editor, or its read-only text. */
  children: ReactNode;
}) {
  const { rows, onFiles, onDelete, uploading, removing, preview, openPreview } = useEvidence(
    entityType,
    entityId,
  );
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      className="relative"
      onDragOver={
        canUpload
          ? (e) => {
              e.preventDefault();
              setDragging(true);
            }
          : undefined
      }
      // Only when the pointer actually left the wrapper: dragleave also fires on
      // the way into the textarea inside it, which made the hint strobe.
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={
        canUpload
          ? (e) => {
              // Without this the browser drops the file into the textarea as text.
              e.preventDefault();
              setDragging(false);
              const files = Array.from(e.dataTransfer.files);
              if (files.length) void onFiles(files);
            }
          : undefined
      }
    >
      {/* One box that owns the border and the focus ring, with the field and an
          attach bar inside it - the way a compose box does it. Never an icon floating
          over the text: the field scrolls, so it would sit on whatever line happens to
          be showing. And never a ring on the field itself: that draws a bottom edge
          across the seam and the bar reads as a second box. The caller strips the
          field's own border, shadow and ring (`EVIDENCE_FIELD_CLASS`). */}
      <div
        className={cn(
          canUpload &&
            'rounded-md border border-input bg-background shadow-sm focus-within:ring-1 focus-within:ring-ring',
        )}
      >
        {children}

        {canUpload && (
          <div className="flex items-center gap-1.5 border-t border-input bg-muted/30 px-1.5 py-1">
            <input
              ref={input}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                // Clear it, or picking the same file twice fires no change event at
                // all - which is exactly what someone does after a failed upload.
                e.target.value = '';
                if (files.length) void onFiles(files);
              }}
            />
            <Button hint={"Attach evidence (document, screenshot, certificate)"}
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
              disabled={uploading}
              onClick={() => input.current?.click()}
            >
              {uploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Paperclip className="size-4" />
              )}
              {uploading ? 'Attaching…' : 'Attach'}
            </Button>
            <span className="truncate text-xs text-muted-foreground">or drop files here</span>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mt-2 space-y-2">
          {rows.map((file) => (
            <AttachmentRow
              key={file.id}
              file={file}
              onDelete={onDelete}
              onPreview={openPreview}
              removing={removing}
            />
          ))}
        </div>
      )}
      {preview}

      {dragging && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center rounded-md border-2 border-dashed border-primary bg-primary/10 text-xs font-medium text-primary">
          Drop to attach
        </div>
      )}
    </div>
  );
}

/** The same list, as a card of its own — for evidence about a whole assessment. */
export function EvidencePanel({
  entityType,
  entityId,
  canUpload,
  description,
}: {
  entityType: AttachmentEntity;
  entityId: string;
  canUpload: boolean;
  description?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Paperclip className="size-4 text-muted-foreground" />
          Evidence
        </CardTitle>
        <CardDescription>
          {description ?? 'Certificates, appreciation emails, client awards — up to 10 MB each.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <EvidenceList entityType={entityType} entityId={entityId} canUpload={canUpload} />
      </CardContent>
    </Card>
  );
}
